import React, { useState, useEffect, useContext } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Autocomplete,
  TextField,
  Chip,
  Button,
  Box,
  Grid,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import SearchIcon from '@mui/icons-material/Search';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import DeselectIcon from '@mui/icons-material/Deselect';
import { CajasContext } from 'src/context/CajasContext/CajasContext';
import esArray from 'src/general_components/functionEsArray';
import FormatListNumberedRtlIcon from '@mui/icons-material/FormatListNumberedRtl';

const namesSections = {
  cuentas_localizaciones: 'Sectores',
  tipo_cajas: 'Tipo de Cajas',
  estados_cajas: 'Estados de Cajas',
  condicion_cajas: 'Condición de Cajas',
};

export const TotalizadoresSearch = (datos) => {
  const [expanded, setExpanded] = useState(true);
  const [selectedItems, setSelectedItems] = useState({});
  const [pendingItems, setPendingItems] = useState({});
  const [cachedCounters, setCachedCounters] = useState({});

  const { dataSearchFormGeneral, setDataSearchFormGeneral, setBusquedaSearchOn, setActualizarListado } = useContext(CajasContext);

  useEffect(() => {
    const counters = datos.responseSearch?.custom_counters;
    if (counters && Object.keys(counters).length > 0) {
      setCachedCounters(counters);
    }
  }, [datos.responseSearch]);

  const raw = datos.responseSearch?.custom_counters || cachedCounters;
  const filteredCounters = datos.responseSearch?.custom_counters_filtered || null;

  // Armar condicion_cajas desde excedida + deformada
  const data = { ...raw };
  const filteredExcedida = esArray(data?.excedida?.items).filter(item => item.id === 'excedida');
  const filteredDeformada = esArray(data?.deformada?.items).filter(item => item.id === 'deformada');
  if (filteredExcedida.length > 0 || filteredDeformada.length > 0) {
    data.condicion_cajas = { items: [...filteredExcedida, ...filteredDeformada] };
  }

  // Secciones visibles (excluir excedida/deformada sueltas)
  const visibleSections = Object.keys(data).filter(
    (k) => namesSections[k] && k !== 'excedida' && k !== 'deformada'
  );

  // Resetear cuando se hace una nueva búsqueda desde el modal
  useEffect(() => {
    if (dataSearchFormGeneral.search_params?.searchModal) {
      setSelectedItems({});
      setPendingItems({});
    }
  }, [dataSearchFormGeneral]);

  const buildOptions = (items, sectionName) => {
    // Ocultar opciones con cant=0 solo si ESE selector tiene items pendientes seleccionados.
    // Si el selector está vacío, mostrar todas las opciones (incluso cant=0) para poder re-seleccionar.
    const sectionHasPendingSelection = (pendingItems[sectionName]?.length || 0) > 0;
    const filteredSectionItems = filteredCounters
      ? esArray(filteredCounters[sectionName]?.items)
      : null;

    return esArray(items)
      .map((item) => {
        let cant = item.cant;
        if (filteredSectionItems) {
          const match = filteredSectionItems.find((fi) => String(fi.id) === String(item.id));
          cant = match ? match.cant : 0;
        }
        return {
          id: item.id,
          nombre: item.nombre,
          cant,
          label: sectionName === 'tipo_cajas'
            ? `${item.id} - ${item.nombre} (${cant})`
            : `${item.nombre} (${cant})`,
        };
      })
      .filter((item) => !sectionHasPendingSelection || item.cant > 0);
  };

  const handleChange = (sectionName, newValue) => {
    setPendingItems((prev) => ({ ...prev, [sectionName]: newValue }));
  };

  const totalPendingSelections = Object.values(pendingItems).reduce(
    (acc, arr) => acc + (arr?.length || 0), 0
  );

  const hasChanges = JSON.stringify(pendingItems) !== JSON.stringify(selectedItems);

  const handleApply = () => {
    setSelectedItems(pendingItems);

    // Armar custom_query igual que antes
    let customQuery = {};
    for (const section in pendingItems) {
      const arr = pendingItems[section];
      if (arr && arr.length > 0) {
        if (section === 'condicion_cajas') {
          customQuery['Flags'] = arr.map((item) => ({ id: item.id, cant: 1 }));
        } else {
          customQuery[section] = arr.map((item) => ({
            id: item.id,
            nombre: item.nombre,
            cant: item.cant,
          }));
        }
      }
    }

    setDataSearchFormGeneral((prev) => ({
      ...prev,
      search_params: {
        ...prev.search_params,
        custom_query: customQuery,
        searchModal: false,
      },
    }));
    setBusquedaSearchOn(true);
    setActualizarListado(true);
  };

  const handleClear = () => {
    setPendingItems({});
    setSelectedItems({});
    setDataSearchFormGeneral((prev) => ({
      ...prev,
      search_params: {
        ...prev.search_params,
        custom_query: {},
        searchModal: false,
      },
    }));
    setBusquedaSearchOn(true);
    setActualizarListado(true);
  };

  const totalSelected = Object.values(selectedItems).reduce(
    (acc, arr) => acc + (arr?.length || 0), 0
  );

  return (
    <Box sx={{ mb: 1.5, mt: 1.5 }}>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        sx={{ borderRadius: '8px !important', overflow: 'hidden', boxShadow: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormatListNumberedRtlIcon sx={{ color: '#3c4b64' }} />
            <FilterAltIcon sx={{ color: '#3c4b64' }} />
            <Typography sx={{ fontWeight: 'bold', color: '#3c4b64', fontSize: 14 }}>
              Totales | Filtros
            </Typography>
            {totalSelected > 0 && (
              <Chip
                size="small"
                label={`${totalSelected} filtro${totalSelected > 1 ? 's' : ''} activo${totalSelected > 1 ? 's' : ''}`}
                color="primary"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            )}
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ bgcolor: '#f5f7fa', pt: 2, pb: 2 }}>
          <Grid container spacing={2} alignItems="flex-start">
            {visibleSections.map((sectionName) => {
              const section = data[sectionName];
              const options = buildOptions(section.items, sectionName);
              const value = pendingItems[sectionName] || [];
              const allSelected = value.length === options.length;

              return (
                <Grid item xs={12} sm={6} md={3} key={sectionName}>
                  <Box
                    sx={{
                      border: '1px solid rgba(0,0,0,0.23)',
                      borderRadius: 1,
                      p: 1,
                      bgcolor: 'white',
                      position: 'relative',
                      '&:hover': { borderColor: 'rgba(0,0,0,0.87)' },
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        position: 'absolute',
                        top: -9,
                        left: 10,
                        px: 0.5,
                        bgcolor: 'white',
                        color: 'text.secondary',
                        lineHeight: 1,
                        fontSize: '0.75rem',
                      }}
                    >
                      {namesSections[sectionName]}
                    </Typography>
                    <Autocomplete
                      multiple
                      size="small"
                      options={options}
                      value={value}
                      onChange={(_, newValue) => handleChange(sectionName, newValue)}
                      isOptionEqualToValue={(opt, val) => opt.id === val.id}
                      disableCloseOnSelect
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                            <Typography variant="body2">{option.label.replace(/ \(\d+\)$/, '')}</Typography>
                            <Chip
                              label={option.cant}
                              size="small"
                              sx={{ height: 18, fontSize: '0.68rem', bgcolor: selected ? 'primary.light' : 'grey.200' }}
                            />
                          </Box>
                        </li>
                      )}
                      renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option.id}
                            label={option.nombre || option.id}
                            size="small"
                            sx={{ fontSize: '0.7rem', maxWidth: 110 }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Buscar..."
                          variant="standard"
                          InputProps={{ ...params.InputProps, disableUnderline: true }}
                        />
                      )}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        borderTop: '1px solid rgba(0,0,0,0.08)',
                        mt: 0.75,
                        pt: 0.5,
                        gap: 0.5,
                      }}
                    >
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<SelectAllIcon fontSize="small" />}
                        onClick={() => handleChange(sectionName, options)}
                        disabled={allSelected}
                        sx={{ fontSize: '0.68rem', py: 0.25, minWidth: 0 }}
                      >
                        Todos
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        color="secondary"
                        startIcon={<DeselectIcon fontSize="small" />}
                        onClick={() => handleChange(sectionName, [])}
                        disabled={value.length === 0}
                        sx={{ fontSize: '0.68rem', py: 0.25, minWidth: 0 }}
                      >
                        Ninguno
                      </Button>
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                        {value.length}/{options.length}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              );
            })}

            {/* Botones alineados al final */}
            <Grid item xs={12} sm={12} md="auto" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: { xs: 0, md: 0 } }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<SearchIcon />}
                onClick={handleApply}
                disabled={!hasChanges && totalSelected === 0}
                sx={{ whiteSpace: 'nowrap', height: 40 }}
              >
                Aplicar
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="secondary"
                startIcon={<FilterAltOffIcon />}
                onClick={handleClear}
                disabled={totalSelected === 0 && totalPendingSelections === 0}
                sx={{ whiteSpace: 'nowrap', height: 40 }}
              >
                Limpiar
              </Button>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default TotalizadoresSearch;
